/**
 * Translate description: Sidebar admin-menu
 */

import { useState, Fragment, useEffect, useContext } from 'react';
import { useLocation, Link as ReactLink, useNavigate } from "react-router-dom";
import { useLanguageContext } from "../../context/LanguageContext";
import UserContext from "../../context/UserContext";

import Box from '@mui/joy/Box';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import CategoryIcon from '@mui/icons-material/Category';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import TranslateIcon from '@mui/icons-material/Translate';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';
import BackpackRoundedIcon from '@mui/icons-material/BackpackRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import RawOnRoundedIcon from '@mui/icons-material/RawOnRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SnippetFolderIcon from '@mui/icons-material/SnippetFolder';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CallMissedOutgoingRoundedIcon from '@mui/icons-material/CallMissedOutgoingRounded';


const linkStyle = { textDecoration: 'none', display: 'block', width: '100%' };

function locateIn(location, regexarr) {
  return !!regexarr.filter(v => {
    const regexp = new RegExp(`^${v}$`);
    return regexp.test(location.pathname);
  }).length;
}

function Toggler({ defaultExpanded = false, locationWhereOpened = [], renderToggle, children }) {
  const [open, setOpen] = useState(defaultExpanded);
  useEffect(() => {
    setOpen(locateIn(location, locationWhereOpened));
  }, [location.pathname]);

  return (
    <Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          transition: '0.2s ease',
          '& > *': {
            overflow: 'hidden',
          },
        }}
      >
        {children}
      </Box>
    </Fragment>
  );
}

function MenuItem({ children, to, selected, icon }) {
  const location = useLocation();
  return (
    <ListItem>
      <ReactLink to={to} style={linkStyle}>
        <ListItemButton selected={locateIn(location, selected)}>
          {icon}
          <ListItemContent>
            <Typography level="title-sm">{children}</Typography>
          </ListItemContent>
        </ListItemButton>
      </ReactLink>
    </ListItem>
  );
}

function SubMenu({ children, title, selected, icon, href }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <ListItem nested>
      <Toggler
        locationWhereOpened={selected}
        renderToggle={({ open, setOpen }) => (
          <ListItemButton onClick={() => {
            (href && location.pathname !== href) ? navigate(href) : setOpen(!open);
          }}>
            {icon}
            <ListItemContent>
              <Typography level="title-sm">{title}</Typography>
            </ListItemContent>
            <KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
          </ListItemButton>
        )}
      >
        <List sx={{ gap: 0.5 }}>{children}</List>
      </Toggler>
    </ListItem>
  );
}

function SubItem({ children, to, selected }) {
  const location = useLocation();
  return (
    <ListItem>
      <ReactLink to={to} style={linkStyle}>
        <ListItemButton selected={locateIn(location, selected)}>{children}</ListItemButton>
      </ReactLink>
    </ListItem>
  )
}


export default function SidebarMenu() {
  const { t } = useLanguageContext();
  const { user } = useContext(UserContext);
  return (
    <>
      <List size="sm" sx={{ gap: 1, '--List-nestedInsetStart': '30px', '--ListItem-radius': (theme) => theme.vars.radius.sm }}>
        <SubMenu
          title={t("Site content")}
          selected={[
            '/pages', '/page/\\d+', '/pages/add/.*',
            '/blogs', '/blogs/.*', '/blog/\\d+',
            '/categories', '/categories/.*', '/category/\\d+',
            '/stores', '/stores/.*', '/store/\\d+',
            '/offers', '/offers/.*', '/offer/\\d+'
          ]}
          icon={<SnippetFolderIcon />}
          href='/pages'
        >
          {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/pages"} selected={['/pages', '/page/\\d+', '/pages/add/.*']} icon={<AutoStoriesRoundedIcon />}>{t("Pages")}</MenuItem>}

          {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/blogs"} selected={['/blogs', '/blogs/.*', '/blog/\\d+']} icon={<LocalOfferIcon />}>{t("Blogs")}</MenuItem>}
          {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/categories"} selected={['/categories', '/categories/.*', '/category/\\d+']} icon={<CategoryIcon />}>{t("Categories")}</MenuItem>}

          {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/stores"} selected={['/stores', '/stores/.*', '/store/\\d+']} icon={<NewspaperOutlinedIcon />}>{t("Stores")}</MenuItem>}
          {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/offers"} selected={['/offers', '/offers/.*', '/offer/\\d+']} icon={<LocalOfferIcon />}>{t("Offers")}</MenuItem>}
        </SubMenu>

        {['admin'].includes(user.role) && <SubMenu
          title={t("Orders")}
          selected={[
            '/orders', '/orders/.*', '/order/\\d+',
            '/transactions', '/transactions/.*', '/transaction/\\d+'
          ]}
          icon={<ShoppingBasketRoundedIcon />}
          href='/orders'
        >
          <MenuItem to={"/orders"} selected={['/orders', '/orders/.*', '/order/\\d+']}>{t("All orders")}</MenuItem>
          <MenuItem to={"/transactions"} selected={['/transactions', '/transactions/.*', '/transaction/\\d+']}>{t("Transactions")}</MenuItem>
        </SubMenu>}

        {['admin', 'moder', 'editor'].includes(user.role) && <SubMenu
          title={t("Strings l10n")}
          selected={['/strings-admin', '/strings-front']}
          icon={<TranslateIcon />}
          href='/strings-front'
        >
          <SubItem to="/strings-front" selected={['/strings-front']}>{t("Front strings")}</SubItem>
          <SubItem to="/strings-admin" selected={['/strings-admin']}>{t("Admin strings")}</SubItem>
        </SubMenu>}

        <SubMenu
          title={t("Analytitcs")}
          selected={['/target_actions', '/visits']}
          icon={<NewspaperOutlinedIcon />}
          href='/target_actions'
        >
          <SubItem to="/target_actions" selected={['/target_actions']}>
            {t("Target Actions")}
          </SubItem>
          <SubItem to="/visits" selected={['/visits']}>
            {t("Visits")}
          </SubItem>
        </SubMenu>

        {['admin', 'moder', 'editor'].includes(user.role) && <SubMenu
          title={t("Frontend")}
          selected={[
            '/frontend/menus', '/frontend/menu/.*', '/frontend/menus/.*', '/frontend/menu/\\d+', '/frontend/menuitems?/\\d+',
            '/frontend/domain-settings', '/frontend/domain-settings/.*'
          ]}
          icon={<ViewQuiltRoundedIcon />}
          href='/frontend/menus'
        >
          <SubItem to="/frontend/menus" selected={['/frontend/menus', '/frontend/menu/.*', '/frontend/menus/.*', '/frontend/menu/\\d+', '/frontend/menuitems?/\\d+']}>{t("Menus")}</SubItem>
          <SubItem to="/frontend/domain-settings" selected={['/frontend/domain-settings', '/frontend/domain-settings/.*']}>{t("Domain settings")}</SubItem>
        </SubMenu>}
      </List>

      <List size="sm" sx={{ gap: 1, mt: 'auto', flexGrow: 0, '--ListItem-radius': (theme) => theme.vars.radius.sm, mb: 1, '--List-nestedInsetStart': '30px', }}>
        {['admin', 'moder', 'editor'].includes(user.role) && <MenuItem to={"/cpadata"} selected={['/cpadata', '/cpadata/.*']} icon={<RawOnRoundedIcon />}>{t("Incoming CPA data")}</MenuItem>}
        {['admin', 'moder', 'editor'].includes(user.role) && <SubMenu
          title={t("Regions")}
          selected={['/regions', '/region/.*', '/regions/.*', '/regiongroups', '/regiongroup/.*', '/regiongroups/.*']}
          icon={<PublicRoundedIcon />}
          href='/regions'
        >
          <SubItem to="/regions" selected={['/regions', '/region/.*', '/regions/.*']}>
            {t("All regions")}
          </SubItem>
          <SubItem to="/regiongroups" selected={['/regiongroups', '/regiongroup/.*', '/regiongroups/.*']}>
            {t("Region groups")}
          </SubItem>
        </SubMenu>}
        {['admin'].includes(user.role) && <SubMenu
          title={t("Admin area")}
          selected={[
            '/users', '/user/.*', '/users/.*',
            '/cpa', '/cpa/.*',
            '/logging', '/logging/.*',
            '/redirects', '/redirects/.*', '/redirect/.*'
          ]}
          icon={<AdminPanelSettingsIcon />}
          href='/users'
        >
          <MenuItem to={"/users"} selected={['/users', '/user/.*', '/users/.*']} icon={<PeopleAltRoundedIcon />}>{t("Users")}</MenuItem>
          <MenuItem to={"/cpa"} selected={['/cpa', '/cpa/.*']} icon={<BackpackRoundedIcon />}>{t("CPA")}</MenuItem>
          <MenuItem to={"/logging"} selected={['/logging', '/logging/.*']} icon={<HistoryRounded />}>{t("Logging")}</MenuItem>
          <MenuItem to={"/redirects"} selected={['/redirects', '/redirects/.*', '/redirect/.*']} icon={<CallMissedOutgoingRoundedIcon />}>{t("Redirects")}</MenuItem>
        </SubMenu>}
        <MenuItem to={"/settings"} selected={['settings']} icon={<SettingsRoundedIcon />}>{t("Settings")}</MenuItem>
      </List>
    </>
  )
}